import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  AutoAwesome,
  Chat,
  CloudUpload,
  Close,
  Delete,
  Difference,
  Done,
  MergeType,
  Save,
} from '@mui/icons-material';
import {
  platformApi,
  Project,
  WritingStudioArtifact,
  WritingStudioChatResult,
  WritingStudioGenerateResult,
  WritingStudioSource,
  WritingStudioSuggestion,
  WritingStudioVersion,
} from '../../services/platformApi';
import { formatTranslatedLabel } from '../../i18n';
import { useLanguage } from '../../LanguageContext';
import { getRequestErrorMessage } from '../../services/api';
import { getStatusColor, languageOptions } from '../projectConstants';
import {
  FlowBlock,
  FlowSectionUnit,
  LayoutUnit,
  ManuscriptV2,
  createHeadingBlock,
  createParagraphBlock,
  ensureV2,
  isFlowSection,
} from './manuscriptSchema';

const storyArtifactTypes = [
  { value: 'book_chapter', label: 'Chapter' },
  { value: 'book_outline', label: 'Outline' },
  { value: 'preface', label: 'Preface' },
  { value: 'acknowledgement', label: 'Acknowledgements' },
  { value: 'introduction', label: 'Introduction' },
  { value: 'afterword', label: 'Afterword' },
  { value: 'custom', label: 'Custom' },
];

interface EditorDraft {
  title: string;
  storyArtifactType: string;
  bodyText: string;
  language: string;
  textDirection: string;
  status: string;
  manuscript: ManuscriptV2 | null;
}

type ManuscriptArea = 'front_matter' | 'content' | 'back_matter';

interface SectionRef {
  area: ManuscriptArea;
  index: number;
}

interface SectionEntry extends SectionRef {
  section: FlowSectionUnit;
}

interface WritingStudioTabProps {
  project: Project | null;
}

const emptyDraft = (project: Project | null): EditorDraft => ({
  title: '',
  storyArtifactType: 'book_chapter',
  bodyText: '',
  language: project?.primary_language_code || 'en',
  textDirection: project?.text_direction || 'ltr',
  status: 'draft',
  manuscript: null,
});

const directionForLanguage = (language: string): 'ltr' | 'rtl' =>
  languageOptions.find((option) => option.value === language)?.direction || (language === 'he' ? 'rtl' : 'ltr');

const manuscriptFromArtifact = (artifact: WritingStudioArtifact): ManuscriptV2 | null => {
  const metadata = artifact.metadata_json || {};
  const manuscript = metadata.extracted_manuscript_json;
  if (!manuscript || typeof manuscript !== 'object') {
    return null;
  }
  const v2 = ensureV2(manuscript, 'text');
  const hasSections = [...v2.front_matter, ...v2.content, ...v2.back_matter].some(isFlowSection);
  return hasSections ? v2 : null;
};

const manuscriptSections = (manuscript: ManuscriptV2 | null): SectionEntry[] => {
  if (!manuscript) {
    return [];
  }
  const entries: SectionEntry[] = [];
  (['front_matter', 'content', 'back_matter'] as ManuscriptArea[]).forEach((area) => {
    manuscript[area].forEach((unit, index) => {
      if (isFlowSection(unit)) {
        entries.push({ area, index, section: unit });
      }
    });
  });
  return entries;
};

const blockPlainText = (block: FlowBlock): string => {
  if (block.type === 'heading' || block.type === 'paragraph') {
    return block.text;
  }
  if (block.type === 'image') {
    return block.caption || '';
  }
  return '';
};

const manuscriptToPlainText = (manuscript: ManuscriptV2): string =>
  manuscriptSections(manuscript)
    .flatMap(({ section }) => [
      section.title,
      ...section.blocks.map(blockPlainText),
    ])
    .map((text) => text.trim())
    .filter(Boolean)
    .join('\n\n');

const updateManuscriptUnit = (
  manuscript: ManuscriptV2,
  ref: SectionRef,
  updater: (section: FlowSectionUnit) => FlowSectionUnit
): ManuscriptV2 => {
  const units = manuscript[ref.area];
  return {
    ...manuscript,
    [ref.area]: units.map((unit, index): LayoutUnit => (
      index === ref.index && isFlowSection(unit) ? updater(unit) : unit
    )),
  };
};

const draftFromArtifact = (artifact: WritingStudioArtifact, project: Project): EditorDraft => ({
  title: artifact.title,
  storyArtifactType: artifact.story_artifact_type,
  bodyText: artifact.body_text || '',
  language: artifact.language || project.primary_language_code || 'en',
  textDirection: artifact.text_direction || project.text_direction || 'ltr',
  status: artifact.status || 'draft',
  manuscript: manuscriptFromArtifact(artifact),
});

const emptyStoryDocumentUpload = {
  title: '',
  description: '',
  textContent: '',
};

export const WritingStudioTab: React.FC<WritingStudioTabProps> = ({ project }) => {
  const { locale, t } = useLanguage();
  const [sources, setSources] = useState<WritingStudioSource[]>([]);
  const [storyArtifacts, setStoryArtifacts] = useState<WritingStudioArtifact[]>([]);
  const [selectedSourceKeys, setSelectedSourceKeys] = useState<string[]>([]);
  const [activeArtifact, setActiveArtifact] = useState<WritingStudioArtifact | null>(null);
  const [draft, setDraft] = useState<EditorDraft>(emptyDraft(project));
  const [versions, setVersions] = useState<WritingStudioVersion[]>([]);
  const [chatTab, setChatTab] = useState('chat');
  const [chatMessage, setChatMessage] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [chatResult, setChatResult] = useState<WritingStudioChatResult | null>(null);
  const [suggestions, setSuggestions] = useState<WritingStudioSuggestion[]>([]);
  const [generationInstructions, setGenerationInstructions] = useState('');
  const [generatedDraft, setGeneratedDraft] = useState<WritingStudioGenerateResult | null>(null);
  const [artifactDialogOpen, setArtifactDialogOpen] = useState(false);
  const [artifactDialogMode, setArtifactDialogMode] = useState<'create' | 'edit'>('create');
  const [storyDocumentDialogOpen, setStoryDocumentDialogOpen] = useState(false);
  const [storyDocumentFile, setStoryDocumentFile] = useState<File | null>(null);
  const [storyDocumentUpload, setStoryDocumentUpload] = useState(emptyStoryDocumentUpload);
  const [selectedSectionRef, setSelectedSectionRef] = useState<SectionRef | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadStudio = useCallback(async (preferredArtifactId?: number) => {
    if (!project) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const snapshot = await platformApi.getWritingStudioSnapshot(project.id);
      const preferredSourceKey = preferredArtifactId ? `artifact:${preferredArtifactId}` : null;
      setSources(snapshot.sources);
      setStoryArtifacts(snapshot.story_artifacts);
      setSelectedSourceKeys((current) => {
        const availableKeys = current.filter((key) => snapshot.sources.some((source) => source.source_key === key));
        if (preferredSourceKey && snapshot.sources.some((source) => source.source_key === preferredSourceKey)) {
          return Array.from(new Set([...availableKeys, preferredSourceKey]));
        }
        return availableKeys;
      });
      setActiveArtifact((current) => {
        const preferredArtifact = preferredArtifactId
          ? snapshot.story_artifacts.find((artifact) => artifact.source_type === 'artifact' && artifact.id === preferredArtifactId)
          : null;
        if (preferredArtifact) {
          return preferredArtifact;
        }
        if (current && snapshot.story_artifacts.some((artifact) => artifact.artifact_key === current.artifact_key)) {
          return snapshot.story_artifacts.find((artifact) => artifact.artifact_key === current.artifact_key) || null;
        }
        return snapshot.story_artifacts.find((artifact) => artifact.editable) || snapshot.story_artifacts[0] || null;
      });
    } catch {
      setError(t('couldNotLoadWritingStudio'));
    } finally {
      setLoading(false);
    }
  }, [project, t]);

  useEffect(() => {
    loadStudio();
  }, [loadStudio]);

  useEffect(() => {
    if (!project) {
      return;
    }
    if (!activeArtifact) {
      setDraft(emptyDraft(project));
      setVersions([]);
      setSelectedSectionRef(null);
      return;
    }
    setDraft(draftFromArtifact(activeArtifact, project));
    setSelectedSectionRef(null);
    setGeneratedDraft(null);
    setSuggestions([]);
    setChatResult(null);
    if (activeArtifact.editable && activeArtifact.source_type === 'artifact') {
      platformApi
        .listWritingStudioArtifactVersions(project.id, activeArtifact.id)
        .then(setVersions)
        .catch(() => setVersions([]));
    } else {
      setVersions([]);
    }
  }, [activeArtifact, project]);

  const selectedSourceSummary = useMemo(
    () => sources.filter((source) => selectedSourceKeys.includes(source.source_key)),
    [selectedSourceKeys, sources]
  );

  const sectionEntries = useMemo(() => manuscriptSections(draft.manuscript), [draft.manuscript]);
  const activeSection = useMemo(() => {
    if (sectionEntries.length === 0) {
      return null;
    }
    if (!selectedSectionRef) {
      return sectionEntries[0];
    }
    return (
      sectionEntries.find(
        (entry) => entry.area === selectedSectionRef.area && entry.index === selectedSectionRef.index
      ) || sectionEntries[0]
    );
  }, [sectionEntries, selectedSectionRef]);

  const updateStructuredManuscript = (updater: (manuscript: ManuscriptV2) => ManuscriptV2) => {
    setDraft((current) => {
      if (!current.manuscript) {
        return current;
      }
      const manuscript = updater(current.manuscript);
      return {
        ...current,
        manuscript,
        bodyText: manuscriptToPlainText(manuscript),
      };
    });
  };

  const updateSectionTitle = (ref: SectionRef, title: string) => {
    updateStructuredManuscript((manuscript) =>
      updateManuscriptUnit(manuscript, ref, (section) => ({ ...section, title }))
    );
  };

  const updateSectionBlock = (ref: SectionRef, blockIndex: number, text: string) => {
    updateStructuredManuscript((manuscript) =>
      updateManuscriptUnit(manuscript, ref, (section) => ({
        ...section,
        blocks: section.blocks.map((block, index) => {
          if (index !== blockIndex || (block.type !== 'heading' && block.type !== 'paragraph')) {
            return block;
          }
          return { ...block, text };
        }),
      }))
    );
  };

  const addParagraphAfterBlock = (ref: SectionRef, blockIndex: number) => {
    updateStructuredManuscript((manuscript) =>
      updateManuscriptUnit(manuscript, ref, (section) => {
        const insertIndex = Math.max(0, Math.min(blockIndex + 1, section.blocks.length));
        return {
          ...section,
          blocks: [
            ...section.blocks.slice(0, insertIndex),
            createParagraphBlock(''),
            ...section.blocks.slice(insertIndex),
          ],
        };
      })
    );
  };

  const addHeadingAfterBlock = (
    ref: SectionRef,
    blockIndex: number,
    level: 'sub_chapter' | 'sub_sub_chapter'
  ) => {
    updateStructuredManuscript((manuscript) =>
      updateManuscriptUnit(manuscript, ref, (section) => {
        const insertIndex = Math.max(0, Math.min(blockIndex + 1, section.blocks.length));
        return {
          ...section,
          blocks: [
            ...section.blocks.slice(0, insertIndex),
            createHeadingBlock(level),
            ...section.blocks.slice(insertIndex),
          ],
        };
      })
    );
  };

  const deleteParagraphBlock = (ref: SectionRef, blockIndex: number) => {
    updateStructuredManuscript((manuscript) =>
      updateManuscriptUnit(manuscript, ref, (section) => ({
        ...section,
        blocks: section.blocks.filter((block, index) => index !== blockIndex || block.type !== 'paragraph'),
      }))
    );
  };

  const deleteHeadingBlock = (ref: SectionRef, blockIndex: number) => {
    updateStructuredManuscript((manuscript) =>
      updateManuscriptUnit(manuscript, ref, (section) => ({
        ...section,
        blocks: section.blocks.filter((block, index) => index !== blockIndex || block.type !== 'heading'),
      }))
    );
  };

  const mergeParagraphWithNext = (ref: SectionRef, blockIndex: number) => {
    updateStructuredManuscript((manuscript) =>
      updateManuscriptUnit(manuscript, ref, (section) => {
        const current = section.blocks[blockIndex];
        const next = section.blocks[blockIndex + 1];
        if (!current || !next || current.type !== 'paragraph' || next.type !== 'paragraph') {
          return section;
        }
        const mergedText = [current.text.trim(), next.text.trim()].filter(Boolean).join('\n');
        return {
          ...section,
          blocks: section.blocks
            .map((block, index) => (index === blockIndex ? { ...current, text: mergedText } : block))
            .filter((_, index) => index !== blockIndex + 1),
        };
      })
    );
  };

  const toggleSource = (sourceKey: string) => {
    setSelectedSourceKeys((current) =>
      current.includes(sourceKey) ? current.filter((key) => key !== sourceKey) : [...current, sourceKey]
    );
  };

  const closeStoryDocumentDialog = () => {
    if (saving) {
      return;
    }
    setStoryDocumentDialogOpen(false);
    setStoryDocumentFile(null);
    setStoryDocumentUpload(emptyStoryDocumentUpload);
    setError(null);
  };

  const closeArtifactDialog = () => {
    if (saving) {
      return;
    }
    setArtifactDialogOpen(false);
    if (project && activeArtifact) {
      setDraft(draftFromArtifact(activeArtifact, project));
    } else {
      setDraft(emptyDraft(project));
    }
  };

  const selectArtifact = (artifact: WritingStudioArtifact) => {
    setActiveArtifact(artifact);
    const sourceKeys = (artifact.metadata_json?.source_keys as string[] | undefined) || [];
    if (sourceKeys.length > 0) {
      setSelectedSourceKeys(sourceKeys);
    }
    setNotice(null);
    setError(null);
  };

  const openCreateArtifactDialog = () => {
    setDraft(emptyDraft(project));
    setVersions([]);
    setArtifactDialogMode('create');
    setArtifactDialogOpen(true);
    setNotice(null);
    setError(null);
  };

  const openEditArtifactDialog = (artifact: WritingStudioArtifact) => {
    selectArtifact(artifact);
    if (project) {
      setDraft(draftFromArtifact(artifact, project));
    }
    setArtifactDialogMode('edit');
    setArtifactDialogOpen(true);
  };

  const handleCreateArtifact = async () => {
    if (!project || !draft.title.trim()) {
      setError(t('titleRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await platformApi.createWritingStudioArtifact(project.id, {
        story_artifact_type: draft.storyArtifactType,
        title: draft.title.trim(),
        body_text: draft.bodyText,
        language: draft.language,
        text_direction: draft.textDirection,
        status: draft.status,
        source_keys: selectedSourceKeys,
        change_note: t('createdFromWritingStudio'),
      });
      setActiveArtifact(created);
      setArtifactDialogOpen(false);
      setNotice(t('storyArtifactCreated'));
      await loadStudio(created.id);
    } catch {
      setError(t('couldNotCreateStoryArtifact'));
    } finally {
      setSaving(false);
    }
  };

  const handleUploadStoryDocument = async () => {
    if (!project || !storyDocumentFile) {
      setError(t('storyDocumentsRequireWordOrPdf'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const uploaded = await platformApi.uploadProjectStoryDocument(project.id, {
        file: storyDocumentFile,
        title: storyDocumentUpload.title.trim() || storyDocumentFile.name,
        description: storyDocumentUpload.description.trim() || undefined,
        text_content: storyDocumentUpload.textContent.trim() || undefined,
      });
      setStoryDocumentDialogOpen(false);
      setStoryDocumentFile(null);
      setStoryDocumentUpload(emptyStoryDocumentUpload);
      setNotice(t('storyDocumentUploaded'));
      await loadStudio(uploaded.id);
    } catch (err) {
      setError(getRequestErrorMessage(err, t('couldNotUploadStoryDocument')));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVersion = async () => {
    if (!project || !activeArtifact?.editable || !draft.title.trim()) {
      setError(t('selectEditableStudioArtifactAndEnterTitle'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const bodyText = draft.manuscript ? manuscriptToPlainText(draft.manuscript) : draft.bodyText;
      const saved = await platformApi.saveWritingStudioArtifactVersion(project.id, activeArtifact.id, {
        title: draft.title.trim(),
        body_text: bodyText,
        language: draft.language,
        text_direction: draft.textDirection,
        status: draft.status,
        change_note: t('manualStudioSave'),
        source_keys: selectedSourceKeys,
        manuscript_json: draft.manuscript ? (draft.manuscript as unknown as Record<string, unknown>) : undefined,
      });
      setActiveArtifact(saved);
      setArtifactDialogOpen(false);
      setNotice(t('savedVersion').replace('{version}', String(saved.current_version_number || '')).trim());
      await loadStudio(saved.id);
    } catch {
      setError(t('couldNotSaveStoryArtifactVersion'));
    } finally {
      setSaving(false);
    }
  };

  const handleChat = async () => {
    if (!project || !chatMessage.trim()) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await platformApi.chatWithWritingStudio(project.id, {
        artifact_id: activeArtifact?.editable ? activeArtifact.id : undefined,
        title: draft.title,
        body_text: draft.bodyText,
        selected_text: selectedText,
        source_keys: selectedSourceKeys,
        message: chatMessage.trim(),
      });
      setChatResult(result);
      setSuggestions(result.suggestions);
      setChatMessage('');
      setChatTab('suggestions');
    } catch {
      setError(t('writerChatCouldNotRespond'));
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!project || selectedSourceKeys.length === 0) {
      setError(t('selectAtLeastOneSourceBeforeGenerating'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await platformApi.generateWritingStudioArtifact(project.id, {
        artifact_type: 'book_chapter',
        requested_title: draft.title || t('newChapter'),
        source_keys: selectedSourceKeys,
        user_instructions: generationInstructions,
      });
      setGeneratedDraft(result);
      setChatTab('generate');
    } catch {
      setError(t('couldNotGenerateChapterFromSelectedSources'));
    } finally {
      setSaving(false);
    }
  };

  const acceptGeneratedDraft = async () => {
    if (!project || !generatedDraft) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await platformApi.createWritingStudioArtifact(project.id, {
        story_artifact_type: generatedDraft.artifact_type,
        title: generatedDraft.title,
        body_text: generatedDraft.body_text,
        language: generatedDraft.language,
        text_direction: generatedDraft.text_direction,
        status: 'ai_draft',
        source_keys: selectedSourceKeys,
        change_note: t('generatedByWriterAgent'),
      });
      await platformApi.saveWritingStudioArtifactVersion(project.id, created.id, {
        title: generatedDraft.title,
        body_text: generatedDraft.body_text,
        language: generatedDraft.language,
        text_direction: generatedDraft.text_direction,
        status: 'ai_draft',
        change_note: t('acceptedGeneratedChapter'),
        source_keys: selectedSourceKeys,
        source_references: generatedDraft.source_references,
        agent_metadata: generatedDraft.metadata,
      });
      setGeneratedDraft(null);
      setActiveArtifact(created);
      setNotice(t('generatedChapterAcceptedAsNewStoryArtifact'));
      await loadStudio();
    } catch {
      setError(t('couldNotAcceptGeneratedChapter'));
    } finally {
      setSaving(false);
    }
  };

  const applySuggestion = (suggestion: WritingStudioSuggestion) => {
    const nextBody = applyInsertion(draft.bodyText, suggestion.text, suggestion.insertion_mode, selectedText);
    setDraft((current) => ({ ...current, bodyText: nextBody }));
    setSuggestions((current) => current.filter((item) => item.suggestion_id !== suggestion.suggestion_id));
    setArtifactDialogMode(activeArtifact?.editable ? 'edit' : 'create');
    setArtifactDialogOpen(true);
    setNotice(t('suggestionAppliedSaveToCreateVersion'));
  };

  const rejectSuggestion = (suggestionId: string) => {
    setSuggestions((current) => current.filter((item) => item.suggestion_id !== suggestionId));
  };

  const renderStructuredSectionEditor = () => {
    if (!draft.manuscript || !activeSection) {
      return null;
    }
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 1, maxHeight: 620, overflow: 'auto' }}>
            <Stack spacing={0.75}>
              {sectionEntries.map((entry, index) => {
                const selected = entry.area === activeSection.area && entry.index === activeSection.index;
                return (
                  <Button
                    key={`${entry.area}-${entry.index}-${entry.section.id}`}
                    variant={selected ? 'contained' : 'text'}
                    color={entry.area === 'content' ? 'primary' : 'inherit'}
                    onClick={() => setSelectedSectionRef({ area: entry.area, index: entry.index })}
                    sx={{ justifyContent: 'flex-start', textAlign: 'start', py: 1 }}
                  >
                    <Box minWidth={0}>
                      <Typography variant="caption" color={selected ? 'inherit' : 'text.secondary'}>
                        {entry.area === 'front_matter' ? t('preface') : `${t('chapter')} ${index + 1}`}
                      </Typography>
                      <Typography fontWeight={700} noWrap>
                        {entry.section.title || t('untitledSection')}
                      </Typography>
                    </Box>
                  </Button>
                );
              })}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Stack spacing={2}>
            <TextField
              label={t('chapterTitle')}
              value={activeSection.section.title}
              onChange={(event) => updateSectionTitle(activeSection, event.target.value)}
              fullWidth
              inputProps={{ dir: draft.textDirection }}
              disabled={!activeArtifact?.editable || saving}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: 26,
                  fontWeight: 800,
                  lineHeight: 1.25,
                },
              }}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() => addParagraphAfterBlock(activeSection, activeSection.section.blocks.length - 1)}
                disabled={!activeArtifact?.editable || saving}
              >
                {t('addParagraph')}
              </Button>
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() =>
                  addHeadingAfterBlock(activeSection, activeSection.section.blocks.length - 1, 'sub_chapter')
                }
                disabled={!activeArtifact?.editable || saving}
              >
                {t('addSubChapter')}
              </Button>
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() =>
                  addHeadingAfterBlock(activeSection, activeSection.section.blocks.length - 1, 'sub_sub_chapter')
                }
                disabled={!activeArtifact?.editable || saving}
              >
                {t('addSubSubChapter')}
              </Button>
            </Stack>
            <Stack spacing={1.5}>
              {activeSection.section.blocks.map((block, blockIndex) => {
                if (block.type === 'heading') {
                  const isSubSub = block.level === 'sub_sub_chapter';
                  return (
                    <Stack key={`${activeSection.section.id}-${blockIndex}`} spacing={0.75}>
                      <TextField
                        label={formatTranslatedLabel(t, block.level)}
                        value={block.text}
                        onChange={(event) => updateSectionBlock(activeSection, blockIndex, event.target.value)}
                        fullWidth
                        inputProps={{ dir: draft.textDirection }}
                        disabled={!activeArtifact?.editable || saving}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontSize: isSubSub ? 18 : 21,
                            fontWeight: isSubSub ? 700 : 800,
                            lineHeight: 1.3,
                          },
                        }}
                      />
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                        <Button
                          size="small"
                          startIcon={<Add />}
                          onClick={() => addHeadingAfterBlock(activeSection, blockIndex, 'sub_chapter')}
                          disabled={!activeArtifact?.editable || saving}
                        >
                          {t('addSubChapter')}
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Add />}
                          onClick={() => addHeadingAfterBlock(activeSection, blockIndex, 'sub_sub_chapter')}
                          disabled={!activeArtifact?.editable || saving}
                        >
                          {t('addSubSubChapter')}
                        </Button>
                        <Tooltip title={t('addParagraph')}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => addParagraphAfterBlock(activeSection, blockIndex)}
                              disabled={!activeArtifact?.editable || saving}
                            >
                              <Add fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('deleteHeading')}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => deleteHeadingBlock(activeSection, blockIndex)}
                              disabled={!activeArtifact?.editable || saving}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  );
                }
                if (block.type === 'paragraph') {
                  const canMergeWithNext = activeSection.section.blocks[blockIndex + 1]?.type === 'paragraph';
                  return (
                    <Stack key={block.id || `${activeSection.section.id}-${blockIndex}`} spacing={0.75}>
                      <TextField
                        label={t('chapterBody')}
                        value={block.text}
                        onChange={(event) => updateSectionBlock(activeSection, blockIndex, event.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                        inputProps={{ dir: draft.textDirection }}
                        disabled={!activeArtifact?.editable || saving}
                      />
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                        <Button
                          size="small"
                          startIcon={<Add />}
                          onClick={() => addHeadingAfterBlock(activeSection, blockIndex, 'sub_chapter')}
                          disabled={!activeArtifact?.editable || saving}
                        >
                          {t('addSubChapter')}
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Add />}
                          onClick={() => addHeadingAfterBlock(activeSection, blockIndex, 'sub_sub_chapter')}
                          disabled={!activeArtifact?.editable || saving}
                        >
                          {t('addSubSubChapter')}
                        </Button>
                        <Tooltip title={t('addParagraph')}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => addParagraphAfterBlock(activeSection, blockIndex)}
                              disabled={!activeArtifact?.editable || saving}
                            >
                              <Add fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('mergeParagraph')}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => mergeParagraphWithNext(activeSection, blockIndex)}
                              disabled={!activeArtifact?.editable || saving || !canMergeWithNext}
                            >
                              <MergeType fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t('deleteParagraph')}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => deleteParagraphBlock(activeSection, blockIndex)}
                              disabled={!activeArtifact?.editable || saving}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  );
                }
                return (
                  <Paper key={`${activeSection.section.id}-${blockIndex}`} variant="outlined" sx={{ p: 1.25 }}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        {formatTranslatedLabel(t, block.type)}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Button
                          size="small"
                          startIcon={<Add />}
                          onClick={() => addHeadingAfterBlock(activeSection, blockIndex, 'sub_chapter')}
                          disabled={!activeArtifact?.editable || saving}
                        >
                          {t('addSubChapter')}
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Add />}
                          onClick={() => addHeadingAfterBlock(activeSection, blockIndex, 'sub_sub_chapter')}
                          disabled={!activeArtifact?.editable || saving}
                        >
                          {t('addSubSubChapter')}
                        </Button>
                        <Tooltip title={t('addParagraph')}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => addParagraphAfterBlock(activeSection, blockIndex)}
                              disabled={!activeArtifact?.editable || saving}
                            >
                              <Add fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
              {activeSection.section.blocks.length === 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    startIcon={<Add />}
                    variant="outlined"
                    onClick={() => addParagraphAfterBlock(activeSection, -1)}
                    disabled={!activeArtifact?.editable || saving}
                  >
                    {t('addParagraph')}
                  </Button>
                  <Button
                    startIcon={<Add />}
                    variant="outlined"
                    onClick={() => addHeadingAfterBlock(activeSection, -1, 'sub_chapter')}
                    disabled={!activeArtifact?.editable || saving}
                  >
                    {t('addSubChapter')}
                  </Button>
                  <Button
                    startIcon={<Add />}
                    variant="outlined"
                    onClick={() => addHeadingAfterBlock(activeSection, -1, 'sub_sub_chapter')}
                    disabled={!activeArtifact?.editable || saving}
                  >
                    {t('addSubSubChapter')}
                  </Button>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    );
  };

  if (!project) {
    return <Typography color="text.secondary">{t('selectProjectToOpenWritingStudio')}</Typography>;
  }

  return (
    <Stack spacing={2}>
      {error && !storyDocumentDialogOpen && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}
      <Grid container spacing={2}>
        <Grid item xs={12} lg={3}>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">{t('sources')}</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={selectedSourceKeys.length.toString()} />
                  <Tooltip title={t('uploadStoryDocument')}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label={t('uploadStoryDocument')}
                        onClick={() => {
                          setStoryDocumentDialogOpen(true);
                          setNotice(null);
                          setError(null);
                        }}
                        disabled={saving}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
              <Stack spacing={1.25}>
                {sources.map((source) => (
                  <Paper
                    key={source.source_key}
                    variant="outlined"
                    sx={{ p: 1.25, borderColor: selectedSourceKeys.includes(source.source_key) ? 'primary.main' : 'divider' }}
                  >
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Checkbox
                        checked={selectedSourceKeys.includes(source.source_key)}
                        onChange={() => toggleSource(source.source_key)}
                        size="small"
                      />
                      <Box minWidth={0}>
                        <Typography fontWeight={700} noWrap>{source.title}</Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" mt={0.5}>
                          <Chip size="small" label={formatTranslatedLabel(t, source.source_type)} variant="outlined" />
                          <Chip size="small" label={source.language} variant="outlined" />
                          <Chip size="small" label={formatTranslatedLabel(t, source.status)} color={getStatusColor(source.status)} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" mt={0.75}>
                          {source.text_preview || t('noPreview')}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
                {sources.length === 0 && (
                  <Typography color="text.secondary">{t('noTextSourcesAvailableYet')}</Typography>
                )}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">{t('storyArtifacts')}</Typography>
                <Tooltip title={t('createStoryArtifact')}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label={t('createStoryArtifact')}
                      onClick={openCreateArtifactDialog}
                      disabled={saving}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              <Stack spacing={1}>
                {storyArtifacts.map((artifact) => (
                  <Button
                    key={artifact.artifact_key}
                    onClick={() => openEditArtifactDialog(artifact)}
                    variant={activeArtifact?.artifact_key === artifact.artifact_key ? 'contained' : 'outlined'}
                    color={artifact.editable ? 'primary' : 'inherit'}
                    sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                  >
                    <Box>
                      <Typography fontWeight={700}>{artifact.title}</Typography>
                      <Typography variant="caption">
                        {formatTranslatedLabel(t, artifact.story_artifact_type)} / {formatTranslatedLabel(t, artifact.status)}
                        {artifact.current_version_number ? ` / v${artifact.current_version_number}` : ''}
                      </Typography>
                    </Box>
                  </Button>
                ))}
                {storyArtifacts.length === 0 && (
                  <Typography color="text.secondary">{t('createFirstStoryArtifactFromEditor')}</Typography>
                )}
              </Stack>
            </Paper>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Box minWidth={0}>
                  <Typography variant="subtitle1">{t('currentDraft')}</Typography>
                  <Typography variant="h6" noWrap>
                    {activeArtifact?.title || t('noStoryArtifactSelected')}
                  </Typography>
                </Box>
                <Button
                  startIcon={<Save />}
                  variant="outlined"
                  onClick={() => (activeArtifact ? openEditArtifactDialog(activeArtifact) : openCreateArtifactDialog())}
                  disabled={saving}
                >
                  {activeArtifact ? t('editStoryArtifact') : t('create')}
                </Button>
                {draft.manuscript && activeArtifact?.editable ? (
                  <Button
                    startIcon={<Save />}
                    variant="contained"
                    onClick={handleSaveVersion}
                    disabled={saving || !draft.title.trim()}
                  >
                    {t('saveVersion')}
                  </Button>
                ) : null}
              </Stack>

              {activeArtifact && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={formatTranslatedLabel(t, draft.storyArtifactType)} variant="outlined" />
                  <Chip size="small" label={draft.language} variant="outlined" />
                  <Chip size="small" label={formatTranslatedLabel(t, draft.status)} color={getStatusColor(draft.status)} />
                  {activeArtifact.current_version_number ? (
                    <Chip size="small" label={`v${activeArtifact.current_version_number}`} variant="outlined" />
                  ) : null}
                </Stack>
              )}

              {!activeArtifact?.editable && activeArtifact && (
                <Alert severity="info">{t('storyPipelineItemCreateStudioArtifact')}</Alert>
              )}

              {draft.manuscript && activeSection ? (
                renderStructuredSectionEditor()
              ) : (
                <Paper variant="outlined" sx={{ p: 1.5, minHeight: 420, maxHeight: 620, overflow: 'auto' }}>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }} dir={draft.textDirection}>
                    {draft.bodyText || t('empty')}
                  </Typography>
                </Paper>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Tabs value={chatTab} onChange={(_, value) => setChatTab(value)} sx={{ mb: 2 }}>
              <Tab value="chat" icon={<Chat />} iconPosition="start" label={t('chat')} />
              <Tab value="suggestions" icon={<Difference />} iconPosition="start" label={t('suggestions')} />
              <Tab value="generate" icon={<AutoAwesome />} iconPosition="start" label={t('generate')} />
            </Tabs>

            {chatTab === 'chat' && (
              <Stack spacing={2}>
                <Typography variant="subtitle1">{t('writerChat')}</Typography>
                <TextField
                  label={t('selectedTextOrTargetNote')}
                  value={selectedText}
                  onChange={(event) => setSelectedText(event.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />
                <TextField
                  label={t('askForEditOrInsertion')}
                  value={chatMessage}
                  onChange={(event) => setChatMessage(event.target.value)}
                  multiline
                  minRows={4}
                  fullWidth
                />
                <Button startIcon={<Chat />} variant="contained" onClick={handleChat} disabled={saving || !chatMessage.trim()}>
                  {t('send')}
                </Button>
                {chatResult && (
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography fontWeight={700}>{t('assistant')}</Typography>
                    <Typography>{chatResult.message}</Typography>
                  </Paper>
                )}
              </Stack>
            )}

            {chatTab === 'suggestions' && (
              <Stack spacing={2}>
                <Typography variant="subtitle1">{t('diffAcceptReject')}</Typography>
                {suggestions.map((suggestion) => (
                  <Paper key={suggestion.suggestion_id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={suggestion.insertion_mode} />
                        <Chip size="small" label={suggestion.language} variant="outlined" />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">{t('currentTarget')}</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {selectedText || draft.bodyText.slice(0, 600) || t('appendToEmptyDraft')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{t('proposedText')}</Typography>
                      <Typography sx={{ whiteSpace: 'pre-wrap' }}>{suggestion.text}</Typography>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" startIcon={<Done />} variant="contained" onClick={() => applySuggestion(suggestion)}>
                          {t('accept')}
                        </Button>
                        <Button size="small" startIcon={<Close />} onClick={() => rejectSuggestion(suggestion.suggestion_id)}>
                          {t('reject')}
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
                {suggestions.length === 0 && <Typography color="text.secondary">{t('noPendingSuggestions')}</Typography>}
              </Stack>
            )}

            {chatTab === 'generate' && (
              <Stack spacing={2}>
                <Typography variant="subtitle1">{t('aiChapterFromSources')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('selectedSources')}: {selectedSourceSummary.map((source) => source.title).join(', ') || t('none')}
                </Typography>
                <TextField
                  label={t('generationInstructions')}
                  value={generationInstructions}
                  onChange={(event) => setGenerationInstructions(event.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                />
                <Button startIcon={<AutoAwesome />} variant="contained" onClick={handleGenerate} disabled={saving || selectedSourceKeys.length === 0}>
                  {t('generateChapter')}
                </Button>
                {generatedDraft && (
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Stack spacing={1.5}>
                      <Typography fontWeight={700}>{generatedDraft.title}</Typography>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">{t('currentDraft')}</Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto' }}>
                            {draft.bodyText || t('empty')}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">{t('generatedDraft')}</Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto' }}>
                            {generatedDraft.body_text}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" startIcon={<Done />} variant="contained" onClick={acceptGeneratedDraft}>
                          {t('acceptAsNewChapter')}
                        </Button>
                        <Button size="small" startIcon={<Close />} onClick={() => setGeneratedDraft(null)}>
                          {t('reject')}
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                )}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
      <Dialog open={artifactDialogOpen} onClose={closeArtifactDialog} fullWidth maxWidth="md">
        <DialogTitle>
          {artifactDialogMode === 'create' ? t('createStoryArtifact') : t('editStoryArtifact')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {artifactDialogMode === 'edit' && activeArtifact && !activeArtifact.editable && (
              <Alert severity="info">{t('storyPipelineItemCreateStudioArtifact')}</Alert>
            )}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                label={t('title')}
                value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                fullWidth
                disabled={artifactDialogMode === 'edit' && !activeArtifact?.editable}
              />
              <FormControl sx={{ minWidth: 170 }} disabled={artifactDialogMode === 'edit' && !activeArtifact?.editable}>
                <InputLabel id="studio-dialog-type-label">{t('type')}</InputLabel>
                <Select
                  labelId="studio-dialog-type-label"
                  label={t('type')}
                  value={draft.storyArtifactType}
                  onChange={(event) => setDraft({ ...draft, storyArtifactType: event.target.value })}
                >
                  {storyArtifactTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {formatTranslatedLabel(t, type.value)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
              <FormControl sx={{ minWidth: 150 }} disabled={artifactDialogMode === 'edit' && !activeArtifact?.editable}>
                <InputLabel id="studio-dialog-language-label">{t('language')}</InputLabel>
                <Select
                  labelId="studio-dialog-language-label"
                  label={t('language')}
                  value={draft.language}
                  onChange={(event) => {
                    const language = event.target.value;
                    setDraft({ ...draft, language, textDirection: directionForLanguage(language) });
                  }}
                >
                  {languageOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 130 }} disabled={artifactDialogMode === 'edit' && !activeArtifact?.editable}>
                <InputLabel id="studio-dialog-direction-label">{t('direction')}</InputLabel>
                <Select
                  labelId="studio-dialog-direction-label"
                  label={t('direction')}
                  value={draft.textDirection}
                  onChange={(event) => setDraft({ ...draft, textDirection: event.target.value })}
                >
                  <MenuItem value="ltr">{t('ltr')}</MenuItem>
                  <MenuItem value="rtl">{t('rtl')}</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 140 }} disabled={artifactDialogMode === 'edit' && !activeArtifact?.editable}>
                <InputLabel id="studio-dialog-status-label">{t('status')}</InputLabel>
                <Select
                  labelId="studio-dialog-status-label"
                  label={t('status')}
                  value={draft.status}
                  onChange={(event) => setDraft({ ...draft, status: event.target.value })}
                >
                  <MenuItem value="draft">{t('draft')}</MenuItem>
                  <MenuItem value="ai_draft">{t('ai_draft')}</MenuItem>
                  <MenuItem value="in_review">{t('inReview')}</MenuItem>
                  <MenuItem value="approved">{t('approved')}</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {draft.manuscript && activeSection ? (
              renderStructuredSectionEditor()
            ) : (
              <TextField
                label={t('storyText')}
                value={draft.bodyText}
                onChange={(event) => setDraft({ ...draft, bodyText: event.target.value })}
                multiline
                minRows={16}
                fullWidth
                inputProps={{ dir: draft.textDirection }}
                disabled={artifactDialogMode === 'edit' && !activeArtifact?.editable}
              />
            )}

            {artifactDialogMode === 'edit' && (
              <>
                <Divider />
                <Typography variant="subtitle1">{t('versionHistory')}</Typography>
                <Stack spacing={1}>
                  {versions.map((version) => (
                    <Paper key={version.version_number} variant="outlined" sx={{ p: 1.25 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Box>
                          <Typography fontWeight={700}>v{version.version_number}: {version.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {version.change_note || t('saved')}
                            {version.created_at ? ` / ${new Date(version.created_at).toLocaleString(locale)}` : ''}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          onClick={() =>
                            setDraft({
                              title: version.title,
                              storyArtifactType: draft.storyArtifactType,
                              bodyText: version.body_text,
                              language: version.language,
                              textDirection: version.text_direction,
                              status: version.status,
                              manuscript: draft.manuscript,
                            })
                          }
                          disabled={!activeArtifact?.editable}
                        >
                          {t('restoreToEditor')}
                        </Button>
                      </Stack>
                    </Paper>
                  ))}
                  {versions.length === 0 && <Typography color="text.secondary">{t('noStudioVersionsYet')}</Typography>}
                </Stack>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeArtifactDialog} disabled={saving}>
            {t('cancel')}
          </Button>
          {artifactDialogMode === 'create' ? (
            <Button
              startIcon={<Add />}
              variant="contained"
              onClick={handleCreateArtifact}
              disabled={saving || !draft.title.trim()}
            >
              {t('create')}
            </Button>
          ) : (
            <Button
              startIcon={<Save />}
              variant="contained"
              onClick={handleSaveVersion}
              disabled={saving || !activeArtifact?.editable || !draft.title.trim()}
            >
              {t('saveVersion')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={storyDocumentDialogOpen} onClose={closeStoryDocumentDialog} fullWidth maxWidth="sm">
        <DialogTitle>{t('uploadStoryDocument')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && storyDocumentDialogOpen && <Alert severity="error">{error}</Alert>}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button variant="outlined" component="label" startIcon={<CloudUpload />}>
                {t('selectStoryDocument')}
                <input
                  type="file"
                  accept=".doc,.docx,.pdf,application/msword,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    setError(null);
                    setStoryDocumentFile(file);
                    if (file && !storyDocumentUpload.title) {
                      setStoryDocumentUpload({ ...storyDocumentUpload, title: file.name });
                    }
                  }}
                />
              </Button>
              <Typography variant="body2" color="text.secondary">
                {storyDocumentFile?.name || t('noFileSelected')}
              </Typography>
            </Stack>
            <TextField
              label={t('title')}
              value={storyDocumentUpload.title}
              onChange={(event) => setStoryDocumentUpload({ ...storyDocumentUpload, title: event.target.value })}
              fullWidth
            />
            <TextField
              label={t('description')}
              value={storyDocumentUpload.description}
              onChange={(event) => setStoryDocumentUpload({ ...storyDocumentUpload, description: event.target.value })}
              fullWidth
            />
            <TextField
              label={t('editableStoryText')}
              value={storyDocumentUpload.textContent}
              onChange={(event) => setStoryDocumentUpload({ ...storyDocumentUpload, textContent: event.target.value })}
              fullWidth
              multiline
              minRows={4}
              inputProps={{ dir: project.text_direction || 'ltr' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStoryDocumentDialog} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={handleUploadStoryDocument}
            disabled={saving || !storyDocumentFile}
          >
            {t('upload')}
          </Button>
        </DialogActions>
      </Dialog>
      {loading && <Typography color="text.secondary">{t('refreshingWritingStudio')}</Typography>}
    </Stack>
  );
};

const applyInsertion = (bodyText: string, suggestionText: string, mode: string, selectedText: string) => {
  if (mode === 'replace' && selectedText && bodyText.includes(selectedText)) {
    return bodyText.replace(selectedText, suggestionText);
  }
  if (mode === 'insert_before' && selectedText && bodyText.includes(selectedText)) {
    return bodyText.replace(selectedText, `${suggestionText}\n\n${selectedText}`);
  }
  if (mode === 'insert_after' && selectedText && bodyText.includes(selectedText)) {
    return bodyText.replace(selectedText, `${selectedText}\n\n${suggestionText}`);
  }
  return [bodyText.trim(), suggestionText.trim()].filter(Boolean).join('\n\n');
};
